import { type ThunkAction } from "redux-thunk";
import { type AnyAction } from "../../../redux/types";
import { addQuantity } from "../../../AddToCart/redux/cartAmounts/actions";
import { batch } from "react-redux";
import { selectProductDescriptor } from "../../../redux/products/productsSelectors";
import { userIsPurchaser, userIsSalesman } from "../../../utils/roles";
import { isPlainLineItem, isSubLineItem, type LineItem, type SubLineItem } from "../../models/CartData";
import { convertProductToImpressionCart, trackRemoveFromCart } from "../../../feature/Analytics/ga/ga";
import { send as deleteCart } from "../../api/ClearCart";
import { send as deleteQuote } from "../../../QuoteCart/api/ClearQuote";
import { ForcedLineItemBuilder, type LineItemBuilder } from "../../../utils/cartUpdating/LineItemUpdate";
import { applyUpdates, newChangeObject, newUpdatesObject } from "../../../utils/cartUpdating/common";
import { groupByFn, isNonNull, splitBy, toMapFn } from "../../../utils/reducers";
import { sendAddEntry, sendBulkEditEntry, sendDeleteEntry, sendDeletePartEntry } from "../../api/cartQuoteCommonApi";
import { refreshPriceInfoData } from "../../../utils/cartUpdating/refreshPriceInfoData";
import { refreshDeliveriesForProduct } from "../../../utils/cartUpdating/delivery/refreshEdd";
import { type PriceData } from "../../../models/PriceData";
import { type RootState } from "../../../redux/store";
import { getDesciptorsForLineItems, trackCartUpdate } from "./actionUtils";
import { validatePackage, validateQty } from "../../../utils/cartUpdating/validations/validateGeneralLineItem";
import { validateEntireProductQty } from "../../../utils/cartUpdating/validations/validateGeneralProduct";
import { apiDeleteCartSection } from "../../api/deleteCartSection";
import {
  selectLineItem,
  selectLineItemsByProduct,
  selectPackageEntry,
  selectSubLineItems,
} from "../selectors/cartSelectors";
import { isKITKITProduct, type KITProductData } from "../../../models/ProductData";
import { getPricePerPiece } from "../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import { type checkoutLoadDataAction } from "~/Cart/redux/actions/checkoutPageActions";
import { selectLineItemsForProduct } from "~/Cart/redux/selectors/productEntrySelectors";
import { trackCartContents, trackClearCart } from "~/feature/Analytics/ga/abandonment";
import { type cartLoadFinishAction } from "~/Cart/pages/CartPage/redux/actions";
import { type updateCartCommentAction } from "~/Cart/redux/actions/cartPageActions";
import {
  BLOCK_SCREEN,
  DELETE_PACKAGE_ENTRY,
  SET_PACKAGE_ENTRIES,
  UNBLOCK_SCREEN,
} from "~/Cart/redux/actions/actionTypes";
import { selectCart } from "~/Cart/redux/selectors/groupedCartSelectors";
import { INVALID_ENTRY_ID } from "~/QuickEntryPopup/constants";

import type { ProductImpression } from "~/feature/Analytics/ga/types";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function deleteSectionSaga(quoteNumber: string): ThunkResult<void> {
  return async (dispatch, getState) => {
    const state = getState();

    if (!quoteNumber) {
      // Are there any quote bundles at all?
      const cartData = selectCart(state);
      const hasBundles = cartData.bundles.length > 0;
      if (!hasBundles) {
        // delete section in this situation is equivalent to deleting the entire cart.
        dispatch(clearCart());
        return;
      }
    }

    dispatch({ type: BLOCK_SCREEN, payload: "spinner.removeProduct" });

    const promise = apiDeleteCartSection(quoteNumber);
    // Await on the promise until the BE manages to remove all products from the section and only then update the UI
    await promise;

    const lineItemsByProduct = selectLineItemsByProduct(state);
    Object.entries(lineItemsByProduct).forEach(([productCode, lineItems]) => {
      const allLineItems = lineItems.map(newChangeObject);

      const filterFunc = quoteNumber
        ? (lineItem: LineItemBuilder) => lineItem.get("quoteNumber") === quoteNumber
        : (lineItem: LineItemBuilder) => !lineItem.get("isBundled");
      const allRemainingLineItems = allLineItems.filter((lineItem) => !filterFunc(lineItem));
      const lineItemsToDelete = allLineItems.filter((lineItem) => filterFunc(lineItem));

      const productDescriptors = getDesciptorsForLineItems(state, productCode, allLineItems);
      const availability = Object.values(productDescriptors)[0]?.availability;
      if (availability) {
        refreshDeliveriesForProduct(allRemainingLineItems, availability);
      }

      if (userIsPurchaser()) {
        lineItemsToDelete.forEach((builder) => {
          const lineItem = state.cart.packEntriesById[builder.get("id")];
          trackRemoval(state, productCode, lineItem);
        });
      }

      // TODO: There shouldn't be so many actions dispatched. Should be one, somehow.
      // TODO: First thing to do is refactor-out the addQuantity action and entire "productAmounts" slice of state
      batch(() => {
        dispatch(sendToRedux(allRemainingLineItems));
        lineItemsToDelete.forEach((builder) => {
          const lineItem = state.cart.packEntriesById[builder.get("id")];
          if (quoteNumber === undefined) {
            dispatch(addQuantity(productCode, lineItem.packCode, -lineItem.quantity, lineItem.priceReference));
          }

          dispatch(deletePackageEntryAction(lineItem.id));
        });
      });
    });

    trackCartContents(getState());

    dispatch({
      type: UNBLOCK_SCREEN,
    });
  };
}

export function deletePackageEntry(packageEntryId: number): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch({ type: BLOCK_SCREEN, payload: "spinner.removeProduct" });

    const state = getState();
    const { cart } = state;
    const lineItemToDelete = cart.packEntriesById[packageEntryId];
    const { productCode } = lineItemToDelete;

    const allLineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);

    const allRemainingLineItems = allLineItems.filter((lineItem) => lineItem.get("id") !== packageEntryId);

    const productDescriptors = getDesciptorsForLineItems(state, productCode, allLineItems);
    refreshPriceInfoData(allRemainingLineItems, productDescriptors);

    const productDescriptor = productDescriptors[lineItemToDelete.priceReference];
    const availability = productDescriptor.availability;
    if (availability) {
      refreshDeliveriesForProduct(allRemainingLineItems, availability);
    }

    allRemainingLineItems.forEach(validateQty);
    validateEntireProductQty(allRemainingLineItems, productDescriptor);
    allRemainingLineItems.forEach(validatePackage);

    await dispatch(sendToHybris(allRemainingLineItems));

    if (isSubLineItem(lineItemToDelete)) {
      const parentLineItem = selectPackageEntry(lineItemToDelete.parentLineItemId)(state);
      sendDeletePartEntry(parentLineItem, lineItemToDelete).then(() => {
        dispatch({
          type: UNBLOCK_SCREEN,
        });
      });
    } else if (lineItemToDelete.entryNumber !== null) {
      sendDeleteEntry(lineItemToDelete).then(() => {
        dispatch({
          type: UNBLOCK_SCREEN,
        });
      });

      if (userIsPurchaser()) {
        trackRemoval(state, productCode, lineItemToDelete);
      }
    }

    batch(() => {
      dispatch(sendToRedux(allRemainingLineItems));
      dispatch(
        addQuantity(
          productCode,
          lineItemToDelete.packCode,
          -lineItemToDelete.quantity,
          lineItemToDelete.priceReference,
        ),
      );

      let isKIT = false;
      if (isSubLineItem(lineItemToDelete)) {
        const parentLineItem = selectLineItem(lineItemToDelete.parentLineItemId)(state);
        const parentDescriptor = selectProductDescriptor(state, parentLineItem);
        isKIT = isKITKITProduct(parentDescriptor.base);
      }

      dispatch(deletePackageEntryAction(packageEntryId, isKIT));
    });
  };
}

function deletePackageEntryAction(packageEntryId: number, isKIT?: boolean) {
  return {
    type: DELETE_PACKAGE_ENTRY,
    payload: {
      packageEntryId,
      isKIT,
    },
  } as const;
}

function trackRemoval(state: RootState, productCode: string, lineItem: LineItem) {
  const productDescriptor = selectProductDescriptor(state, {
    productCode,
    priceReference: lineItem.priceReference,
  });
  const pricePerPiece = getPricePerPiece(lineItem);
  const productImpression = convertProductToImpressionCart(
    productDescriptor.base,
    lineItem,
    productDescriptor.availability,
    pricePerPiece,
  );
  trackRemoveFromCart([productImpression]);
}

export function clearCart(): ThunkResult<Promise<unknown>> {
  return (dispatch, getState) => {
    const isSalesman = userIsSalesman();
    const deletionPromise = isSalesman ? deleteQuote() : deleteCart();

    if (!isSalesman) {
      const state = getState();
      const packEntries = Object.values(state.cart.packEntriesById);
      const impressionsMap = packEntries
        .map((packEntry) => {
          const productDescriptor = selectProductDescriptor(state, {
            productCode: packEntry.productCode,
            priceReference: packEntry.priceReference,
          });
          return convertProductToImpressionCart(
            productDescriptor.base,
            packEntry,
            productDescriptor.availability,
            getPricePerPiece(packEntry),
          );
        })
        .reduce(
          (map, impression) => {
            if (!map[impression.id]) {
              map[impression.id] = impression;
            } else {
              map[impression.id].quantity = (map[impression.id].quantity || 0) + (impression.quantity || 0);
            }
            return map;
          },
          {} as { [id: string]: ProductImpression },
        );

      trackRemoveFromCart(Object.values(impressionsMap));
    }

    dispatch({ type: BLOCK_SCREEN });

    deletionPromise.then(() => dispatch(clearCartAction())).finally(() => dispatch({ type: UNBLOCK_SCREEN }));

    if (!isSalesman) {
      trackClearCart();
    }

    return deletionPromise;
  };
}

function internalUpdateQuantity(qty: number, packageEntryId: number): ThunkResult<LineItemBuilder[]> {
  return (dispatch, getState) => {
    const state = getState();

    const packageEntry = state.cart.packEntriesById[packageEntryId];
    const productCode = packageEntry.productCode;

    const potentiallyUpdatedItems = [];

    if (isPlainLineItem(packageEntry) && packageEntry.optionalPartIds.length) {
      const productDescriptor = selectProductDescriptor(state, packageEntry);
      packageEntry.optionalPartIds.forEach((id) => {
        const subLineItem = selectLineItem(id)(state);
        const partEntry = (productDescriptor.base as KITProductData).optionalParts.find(
          (item) => item.product.code === subLineItem.productCode,
        )!;
        const subQuantity = qty * partEntry.quantity;
        potentiallyUpdatedItems.push(...dispatch(internalUpdateQuantity(subQuantity, id)));
      });
    }

    const allLineItems = selectLineItemsForProduct(state, { productCode }).map(newChangeObject);

    const productDescriptors = getDesciptorsForLineItems(state, productCode, allLineItems);

    const editedLineItem = allLineItems.filter((item) => item.get("id") === packageEntryId)[0];

    const nonContractLineItems = allLineItems.filter((item) => !item.get("contractNumber"));

    editedLineItem.set("quantity", qty);

    refreshPriceInfoData(allLineItems, productDescriptors);

    const productDescriptor = Object.values(productDescriptors)[0];
    const availability = productDescriptor.availability;
    if (availability) {
      refreshDeliveriesForProduct(nonContractLineItems, availability);
    }

    validateQty(editedLineItem);
    validateEntireProductQty(allLineItems, productDescriptor);

    potentiallyUpdatedItems.push(...allLineItems);

    return potentiallyUpdatedItems;
  };
}

export function updateQuantity(qty: number, packageEntryId: number): ThunkResult<void> {
  return async (dispatch) => {
    const allLineItems = dispatch(internalUpdateQuantity(qty, packageEntryId));

    dispatch(sendToRedux(allLineItems));

    const lineItemsToSend = allLineItems.filter((lineItem) => !isSubLineItem(lineItem.getOriginal()));

    dispatch(sendToHybris(lineItemsToSend.length > 0 ? lineItemsToSend : allLineItems));
  };
}

export function sendToRedux(lineItems: LineItemBuilder[]): ThunkResult<void> {
  return (dispatch, getState) => {
    const itemsToUpdate = lineItems.filter((item) => item.hasUpdates());
    if (itemsToUpdate.length === 0) {
      return;
    }

    const cart = getState().cart;

    const data = lineItems
      .map((item) => ({
        item: item.build(),
        productCode: item.getProductCode(),
      }))
      .filter(({ item }) => isNonNull(item))
      .map(({ item, productCode }) => {
        const packageEntry = item as LineItem;
        const oldPackageEntry = cart.packEntriesById[packageEntry.id];

        return {
          packageEntry,
          oldPackageEntry,
          productCode,
        };
      })
      .filter(({ packageEntry }) => {
        const cartEntry = cart.cartEntriesById[packageEntry.cartEntryId];
        // SCHAEFF-32334
        // if the cart doesn't have the product then cartEntryId will be -1, in these cases, this returns an empty array
        // we make an exception for -1 values so we can still set the package entry
        if (!cartEntry && packageEntry.cartEntryId !== -1) {
          console.debug(
            `Skipping update to line entry ${packageEntry.id} as cart entry ${packageEntry.cartEntryId} is missing`,
          );
          return false;
        }
        return true;
      });
    dispatch(setPackageEntriesAction(data));
  };
}

function setPackageEntriesAction(data: { packageEntry: LineItem; oldPackageEntry?: LineItem; productCode: string }[]) {
  return {
    type: SET_PACKAGE_ENTRIES,
    payload: data,
  } as const;
}

export const updatePackageEntryPk = (packageEntryId: number, pk: number) => updatePackageEntry(packageEntryId, { pk });

export const updateDate = (date: Date, packageEntryId: number) =>
  updatePackageEntry(
    packageEntryId,
    { deliveryDate: date, manuallySetRDD: true },
    { cascadeToOptionalParts: true, updateDeliveries: true },
  );

export const clearManualDate = (packageEntryId: number): ThunkResult<void> => {
  return (dispatch, getState) => {
    const edd = getState().cart.packEntriesById[packageEntryId].edd;
    let rdd = null;
    if (edd?.type === "estimate") {
      rdd = edd.date;
    }

    return dispatch(
      updatePackageEntry(packageEntryId, { deliveryDate: rdd, manuallySetRDD: false }, { updateDeliveries: true }),
    );
  };
};

export const updateComment = (comment: string, packageEntryId: number) =>
  updatePackageEntry(packageEntryId, { comment });

export const updateInternalComment = (comment: string, packageEntryId: number) =>
  updatePackageEntry(packageEntryId, { internalComment: comment });

export function toggleCommentVisibility(packageEntryId: number): ThunkResult<void> {
  return (dispatch, getState) => {
    const newShowComments = !getState().cart.packEntriesById[packageEntryId].showComments;
    dispatch(updatePackageEntry(packageEntryId, { showComments: newShowComments }));
  };
}

export const updateOfferPrice = (packageId: number, newPrice?: PriceData) =>
  updatePackageEntry(packageId, {
    offerPrice: newPrice,
    priceOverriden: !!newPrice,
  });

export const updateTargetPrice = (packageId: number, newPrice?: PriceData) =>
  updatePackageEntry(packageId, { targetPrice: newPrice });

interface UpdateOptions {
  /**
   * Whether the update should affect all optional parts as well
   */
  cascadeToOptionalParts?: boolean;
  updateDeliveries?: boolean;
}

export function updatePackageEntry(
  packageEntryId: number,
  updates: Partial<LineItem>,
  options?: UpdateOptions,
): ThunkResult<Promise<unknown>> {
  return (dispatch, getState) => {
    const state = getState();
    const packageEntry = selectPackageEntry(packageEntryId)(state);

    const allLineItems = selectLineItemsForProduct(state, { productCode: packageEntry.productCode }).map(
      newChangeObject,
    );
    const productDescriptors = getDesciptorsForLineItems(state, packageEntry.productCode, allLineItems);
    const editedLineItem = allLineItems.filter((item) => item.get("id") === packageEntryId)[0];
    applyUpdates(editedLineItem, updates as Partial<LineItem>);

    let list;
    if (options?.updateDeliveries) {
      const availability = Object.values(productDescriptors)[0].availability;
      if (availability) {
        refreshDeliveriesForProduct(allLineItems, availability);
      }

      list = [...allLineItems];
    } else {
      list = [editedLineItem];
    }

    if (options?.cascadeToOptionalParts && isPlainLineItem(packageEntry)) {
      packageEntry.optionalPartIds.forEach((id) => {
        const subLineItem = selectPackageEntry(id)(state);
        const subLineItemUpdate = newUpdatesObject(subLineItem, updates as Partial<LineItem>);
        list.push(subLineItemUpdate);
      });
    }

    dispatch(sendToRedux(list));
    return dispatch(sendToHybris(list));
  };
}

export function sendToHybris(lineItems: LineItemBuilder[]): ThunkResult<Promise<unknown>> {
  return async (dispatch, getState) => {
    if (lineItems.length === 0) {
      return;
    }

    const state = getState();

    const [subLineItems, plainLineItems] = splitBy(lineItems, (item) => {
      // @ts-ignore
      const parentLineItemId = item.get("parentLineItemId");
      return parentLineItemId !== undefined && parentLineItemId !== null;
    });

    // These are processed normally
    lineItems = plainLineItems;

    if (subLineItems.length) {
      const parentLineItems = Object.values(
        toMapFn(
          subLineItems.map((i) => {
            // @ts-ignore
            const parentId = i.get("parentLineItemId") as number;
            return selectPackageEntry(parentId)(state);
          }),
          (i) => i.id,
        ),
      ) as LineItem[];

      const lineItemsByCartEntry = groupByFn(parentLineItems, (i) => i.cartEntryId);

      // Send to hybris works per unique cartEntry (product). So be it.
      Object.values(lineItemsByCartEntry)
        .map((group) => group.map((item) => new ForcedLineItemBuilder(item, item.productCode)))
        .forEach((group) => dispatch(sendToHybris(group)));
    }

    const packageEntries = lineItems.map((item) => item.buildForSending()).filter(isNonNull);
    // eslint-disable-next-line prefer-const
    let [newEntries, editedEntries] = splitBy(packageEntries, (item) => item.pk === INVALID_ENTRY_ID);

    for (const item of newEntries) {
      const descriptor = selectProductDescriptor(state, {
        productCode: item.productCode,
        priceReference: item.priceReference,
      });
      await sendAddEntry(item, descriptor, selectSubLineItems(item.id)(state))
        .then((pk) => dispatch(updatePackageEntryPk(item.id, pk)))
        .catch(console.error);
    }

    let res;
    if (editedEntries.length > 0) {
      if (!userIsSalesman()) {
        lineItems
          .filter((item) => item.get("pk") !== INVALID_ENTRY_ID)
          .forEach((item) => {
            const descriptor = selectProductDescriptor(state, {
              productCode: item.getProductCode(),
              priceReference: item.get("priceReference"),
            });
            trackCartUpdate(item, descriptor);
          });
      }

      editedEntries = editedEntries.filter((item) => {
        const itemFromState = selectPackageEntry(item.id)(state);
        if (!itemFromState) {
          console.debug(`Skipping update to line entry ${item.id} as it is missing from the state`);
          return false;
        }
        return true;
      });

      const subLineItemsByParentId: Dict<SubLineItem[]> = {};
      editedEntries.forEach((item) => (subLineItemsByParentId[item.id] = selectSubLineItems(item.id)(state)));

      try {
        await sendBulkEditEntry(editedEntries, subLineItemsByParentId);
        res = true;
      } catch (e) {
        console.error(e);
        res = false;
      }
    } else {
      res = true;
    }

    trackCartContents(getState());

    return res;
  };
}

function clearCartAction() {
  return {
    type: "CLEAR_CART",
  } as const;
}

export type CartActions =
  | ReturnType<typeof setPackageEntriesAction>
  | ReturnType<typeof deletePackageEntryAction>
  | ReturnType<typeof checkoutLoadDataAction>
  | ReturnType<typeof cartLoadFinishAction>
  | ReturnType<typeof clearCartAction>
  | ReturnType<typeof updateCartCommentAction>;
